<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  type ShiftsCalendarSummary,
  type ShiftsCalendarTotalSummary,
} from '@/api/shifts';
import { formatCurrency } from '@/utils/currency';
import { useResponsiveTable } from '@/composables/useResponsiveTable';

interface Props {
  summary: ShiftsCalendarSummary[];
  totalSummary: ShiftsCalendarTotalSummary;
}

const props = defineProps<Props>();
const { tableProps } = useResponsiveTable({ stack: false });
const headers = [
  { title: 'Precio por turno', value: 'price' },
  { title: 'Costo Bruto por turno', value: 'cost' },
  { title: 'Cantidad de Turnos', value: 'totalShifts', cellProps: { class: 'font-weight-bold text-center' } },
  { title: 'Ingreso Total', value: 'totalPrice' },
  { title: 'Costo Bruto Total', value: 'totalCost' },
  { title: 'Margen Bruto $', value: 'totalProfit' },
  { title: 'Margen Bruto %', value: 'margin' },
];

const expanded = ref([]);
const hasCostBreakdown = computed(() =>
  props.totalSummary.salaryBasedCost !== undefined &&
  props.totalSummary.perShiftCost !== undefined &&
  (props.totalSummary.salaryBasedCost > 0 || props.totalSummary.perShiftCost > 0),
);
</script>

<template>
  <v-expansion-panels class="mb-12">
    <v-expansion-panel>
      <v-expansion-panel-title class="font-weight-bold text-body-2">
        Resumen Mensual
      </v-expansion-panel-title>
      <v-expansion-panel-text class="overflow-x-auto">
        <v-data-table
          v-bind="tableProps"
          v-model:expanded="expanded"
          :headers="headers"
          :header-props="{ class: 'font-weight-bold' }"
          :items="[props.totalSummary]"
          class="elevation-1"
          show-expand
        >
          <template #[`item.totalPrice`]="{ item }">
            <v-chip color="success">
              {{ formatCurrency(item.totalPrice) }}
            </v-chip>
          </template>
          <template #[`item.totalCost`]="{ item }">
            <v-chip color="error">
              {{ formatCurrency(item.totalCost) }}
              <v-tooltip
                v-if="hasCostBreakdown"
                activator="parent"
                location="bottom"
              >
                <div class="text-caption">
                  <p>Costo Sueldos: {{ formatCurrency(item.salaryBasedCost ?? 0) }}</p>
                  <p>Costo por Turno: {{ formatCurrency(item.perShiftCost ?? 0) }}</p>
                </div>
              </v-tooltip>
            </v-chip>
          </template>
          <template #[`item.totalProfit`]="{ item }">
            <v-chip color="primary">
              {{ formatCurrency(item.totalProfit) }}
            </v-chip>
          </template>
          <template #[`item.margin`]="{ item }">
            <v-chip color="primary">
              {{ item.margin }} %
            </v-chip>
          </template>
          <template #expanded-row>
            <tr
              v-for="row in props.summary"
              :key="`${row.price}${row.cost}`"
            >
              <td>
                <v-chip color="grey-darken-3">
                  {{ formatCurrency(row.price) }}
                </v-chip>
              </td>
              <td>
                <v-chip color="grey-darken-3">
                  {{ formatCurrency(row.cost) }}
                </v-chip>
              </td>
              <td class="text-center">
                {{ row.totalShifts }}
              </td>
              <td>
                <v-chip color="grey-darken-3">
                  {{ formatCurrency(row.totalPrice) }}
                </v-chip>
              </td>
              <td>
                <v-chip color="grey-darken-3">
                  {{ formatCurrency(row.totalCost) }}
                </v-chip>
              </td>
              <td>
                <v-chip color="grey-darken-3">
                  {{ formatCurrency(row.totalProfit) }}
                </v-chip>
              </td>
              <td>
                <v-chip color="grey-darken-3">
                  {{ row.margin }} %
                </v-chip>
              </td>
            </tr>
          </template>
          <template #[`item.data-table-expand`]="{ internalItem, toggleExpand, isExpanded }">
            <v-btn
              :color="isExpanded(internalItem) ? 'secondary' : 'primary'"
              variant="outlined"
              size="x-small"
              :class="`${isExpanded(internalItem) ? 'bg-secondary-lighten-4' : 'bg-primary-lighten-4'}`"
              icon
              @click="toggleExpand(internalItem)"
            >
              <v-icon :icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
              <v-tooltip
                activator="parent"
                location="top"
              >
                {{ isExpanded(internalItem) ? 'Ocultar detalles' : 'Ver detalles' }}
              </v-tooltip>
            </v-btn>
          </template>
        </v-data-table>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
